import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

/* ---------------- Validation Schema ---------------- */

const schema = Yup.object({
  isChangePassword: Yup.boolean(),
  currentPassword: Yup.string().when("isChangePassword", {
    is: true,
    then: (schema) =>
      schema
        .required("Current password is required")
        .min(8, "Password must be at least 8 characters"),
    otherwise: (schema) => schema.notRequired(),
  }),
  newPassword: Yup.string()
    .required("New password is required")
    .min(8, "Password must be at least 8 characters"),
  confirmPassword: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("newPassword")], "Passwords do not match"),
});

/* ---------------- Component ---------------- */

const UpdatePassword = () => {
  const { setUser, logout, user } = useAuth();
  const navigate = useNavigate();

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [isChangePassword, setIsChangePassword] = useState(false);

  useEffect(()=>{
    setIsChangePassword(user?.passwordUpdated !== false);
  },[user?.passwordUpdated])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  const onSubmit = async (data) => {
    try {
      await axiosInstance.post("/auth/update-password", {
        userId: user?._id,
        newPassword: data.newPassword,
        isChangePassword,
        currentPassword: data.currentPassword
      });

      toast.success("Password updated successfully");

      const updatedUser = {
        ...user,
        passwordUpdated: true,
      };  
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      navigate('/list')
    } catch (err) {
        console.log(err)
      toast.error(
        err?.response?.data?.message || "Failed to update password"
      );
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor="#f4f6f8"
    >
      <Paper
        elevation={3}
        sx={{
          width: 420,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Update Password
        </Typography>

        {user?.passwordUpdated == false && <Typography
          variant="body2"
          color="text.secondary"
          mb={3}
        >
          Please update your password to continue
        </Typography>}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Current Password */}
          {user?.passwordUpdated !== false && <TextField
            label="Current Password"
            type={showCurrentPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            {...register("currentPassword")}
            error={!!errors.currentPassword}
            helperText={errors.currentPassword?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() =>
                      setShowCurrentPassword((p) => !p)
                    }
                  >
                    {showCurrentPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />}

          {/* New Password */}
          <TextField
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            {...register("newPassword")}
            error={!!errors.newPassword}
            helperText={errors.newPassword?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() =>
                      setShowNewPassword((p) => !p)
                    }
                  >
                    {showNewPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Confirm Password */}
          <TextField
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            {...register("confirmPassword")}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() =>
                      setShowConfirmPassword((p) => !p)
                    }
                  >
                    {showConfirmPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={22} />
            ) : (
              "Update Password"
            )}
          </Button>
        </form>

        {/* Back to Login */}
        {user?.passwordUpdated == false && <Box mt={3} textAlign="center">
          <Link
            component="button"
            variant="body2"
            onClick={() => {navigate("/login"); logout()}}
            underline="hover"
          >
            Back to Login
          </Link>
        </Box>}
      </Paper>
    </Box>
  );
};

export default UpdatePassword;
